import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { LayoutGrid } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supplyService, type WorkTrayMode } from '@/services/supplyService';
import { invalidateAllSupplyTableCaches } from '@/utils/supplyTableCache';

const WORK_TRAY_MODE_RESET_FLAG = 'supply_resetScopeOnWorkTrayModeChange';

export function SupplyWorkTraySettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role?.id === 1;

  const settings = useQuery({
    queryKey: ['supply-organization-settings'],
    queryFn: () => supplyService.getOrganizationSettings(),
  });

  const patch = useMutation({
    mutationFn: (work_tray_mode: WorkTrayMode) =>
      supplyService.updateOrganizationSettings({ work_tray_mode }),
    onSuccess: () => {
      try {
        sessionStorage.setItem(WORK_TRAY_MODE_RESET_FLAG, '1');
      } catch {
        /* ignore */
      }
      void queryClient.invalidateQueries({ queryKey: ['supply-organization-settings'] });
      invalidateAllSupplyTableCaches();
    },
  });

  const mode: WorkTrayMode = settings.data?.work_tray_mode ?? 'supplier';

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <LayoutGrid className="w-5 h-5 text-stocker-primary" />
          {t('settings.supplyWorkTray.title')}
        </CardTitle>
        <CardDescription>{t('settings.supplyWorkTray.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {settings.isError ? (
          <p className="text-sm text-red-600">{t('settings.saveError')}</p>
        ) : settings.isLoading ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : (
          <fieldset className="space-y-3" disabled={!canEdit || patch.isPending}>
            <legend className="sr-only">{t('settings.supplyWorkTray.title')}</legend>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="radio"
                name="work_tray_mode"
                className="mt-1 h-4 w-4 border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={mode === 'supplier'}
                onChange={() => patch.mutate('supplier')}
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  {t('settings.supplyWorkTray.modeSupplier')}
                </span>
                <span className="block text-xs text-gray-500">
                  {t('settings.supplyWorkTray.modeSupplierHint')}
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="radio"
                name="work_tray_mode"
                className="mt-1 h-4 w-4 border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={mode === 'product_group'}
                onChange={() => patch.mutate('product_group')}
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  {t('settings.supplyWorkTray.modeProductGroup')}
                </span>
                <span className="block text-xs text-gray-500">
                  {t('settings.supplyWorkTray.modeProductGroupHint')}
                </span>
              </span>
            </label>
          </fieldset>
        )}
        {!canEdit ? (
          <p className="mt-2 text-xs text-gray-500">{t('settings.supplyWorkTray.readOnly')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
